import { useEffect } from "react";

/**
 * Post-combat celebration overlay. Chibi does a triumph pose while
 * XP / gold / drop scroll up in sequence. Auto-dismisses after ~2.6s.
 * Rendered by Game.jsx (not CombatOverlay) so it survives combat-state
 * cleanup and the CombatOverlay unmount.
 */
export default function VictorySplash({ chibi, cls, name, victory, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-hidden"
      data-testid="victory-splash"
      onClick={onDone}
      style={{
        background:
          "radial-gradient(60% 50% at 50% 55%, rgba(229,195,74,0.18), rgba(5,5,5,0.95))",
      }}
    >
      {/* Golden rays */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "conic-gradient(from 0deg at 50% 55%, rgba(229,195,74,0.25) 0deg, transparent 20deg, rgba(229,195,74,0.25) 40deg, transparent 60deg, rgba(229,195,74,0.25) 80deg, transparent 100deg, rgba(229,195,74,0.25) 120deg, transparent 140deg, rgba(229,195,74,0.25) 160deg, transparent 180deg, rgba(229,195,74,0.25) 200deg, transparent 220deg, rgba(229,195,74,0.25) 240deg, transparent 260deg, rgba(229,195,74,0.25) 280deg, transparent 300deg, rgba(229,195,74,0.25) 320deg, transparent 340deg)",
          animation: "victory-rays 6s linear infinite",
          filter: "blur(1px)",
        }}
      />

      <div className="relative flex flex-col items-center gap-3">
        <div
          className="font-heading tracking-[0.4em] uppercase text-[#E5C34A] text-sm"
          data-testid="victory-headline"
        >
          Victory
        </div>

        {/* Chibi in triumph pose */}
        <div
          className="relative"
          style={{ width: 200, height: 260, animation: "victory-cheer 0.8s ease-out both" }}
        >
          {chibi ? (
            <img
              src={chibi}
              alt={`${cls} triumph`}
              draggable={false}
              className="pointer-events-none select-none"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter:
                  "drop-shadow(0 0 12px rgba(229,195,74,0.6)) drop-shadow(0 4px 6px rgba(0,0,0,0.7))",
                transformOrigin: "center bottom",
              }}
            />
          ) : (
            <div className="w-full h-full border-2 border-[#E5C34A] flex items-center justify-center bg-[#0a0a0a]">
              <span className="font-heading tracking-widest text-[#E5C34A]">{cls}</span>
            </div>
          )}
        </div>

        {/* Name */}
        <div className="font-heading text-sm tracking-widest uppercase text-[#E2DFD8]">
          {name}
        </div>
        <div className="font-body text-xs italic text-[#B0ADA5]">
          The {victory.enemy_name} lies still.
        </div>

        {/* Rewards column, staggered rise */}
        <div className="flex flex-col items-center gap-1 mt-3" data-testid="victory-rewards">
          <RewardRow tone="#E5C34A" label={`+${victory.xp} XP`} delay="0.30s" testid="victory-xp" />
          <RewardRow tone="#B89C3A" label={`+${victory.gold} Gold`} delay="0.55s" testid="victory-gold" />
          {victory.drop && (
            <RewardRow
              tone={dropTint(victory.drop.rarity)}
              label={victory.drop.name}
              delay="0.85s"
              testid="victory-drop"
            />
          )}
        </div>

        <div className="mt-4 font-mono text-[10px] tracking-widest text-[#666] uppercase animate-pulse">
          Tap anywhere to continue
        </div>
      </div>

      <style>{`
        @keyframes victory-cheer {
          0%   { transform: translateY(30px) scale(0.6) rotate(-10deg); opacity: 0; }
          40%  { transform: translateY(-8px) scale(1.1) rotate(4deg); opacity: 1; }
          70%  { transform: translateY(2px) scale(0.98) rotate(-2deg); }
          100% { transform: translateY(0) scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes victory-rise {
          0%   { transform: translateY(20px); opacity: 0; }
          30%  { opacity: 1; }
          100% { transform: translateY(-8px); opacity: 1; }
        }
        @keyframes victory-rays {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function RewardRow({ tone, label, delay, testid }) {
  return (
    <div
      data-testid={testid}
      className="font-heading text-lg tracking-widest uppercase"
      style={{
        color: tone,
        textShadow: `0 0 8px ${tone}55, 0 0 2px #000`,
        animation: `victory-rise 0.7s ${delay} both`,
        opacity: 0,
      }}
    >
      {label}
    </div>
  );
}

function dropTint(rarity) {
  const RARITY = {
    common: "#B0ADA5",
    uncommon: "#6E8B3D",
    rare: "#4A7BC7",
    epic: "#8A2BE2",
    legendary: "#E5C34A",
    mythic: "#E33333",
  };
  return RARITY[rarity] || "#B0ADA5";
}
